function cleanText(value, fallback = "") {
  const text = String(value || "").trim();
  return text || fallback;
}

function toInt(value) {
  return Number(value || 0);
}

function titleCase(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function inferActionType(action = {}) {
  const explicit = cleanText(action.action_type || action.event_type).toLowerCase();
  if (explicit) {
    if (explicit.includes("reply")) return "reply";
    if (explicit.includes("disposition")) return "disposition";
    if (explicit.includes("assign")) return "assignment";
    if (explicit.includes("release") || explicit.includes("unassign")) return "release";
    if (explicit.includes("escalate")) return "escalation";
    return explicit;
  }
  const summary = cleanText(action.summary).toLowerCase();
  if (summary.includes("reply")) return "reply";
  if (summary.includes("disposition") || summary.includes("outcome")) return "disposition";
  if (summary.includes("assign") || summary.includes("reassign")) return "assignment";
  if (summary.includes("release") || summary.includes("unassign")) return "release";
  if (summary.includes("escalat")) return "escalation";
  return "review";
}

function reviewTone(actionType) {
  if (actionType === "disposition") {
    return "overdue";
  }
  if (actionType === "reply" || actionType === "escalation") {
    return "stale";
  }
  return "ready";
}

function reviewLabel(actionType) {
  const labels = {
    disposition: "Review outcome",
    reply: "Confirm reply",
    escalation: "Review escalation",
    assignment: "Review assignment",
    release: "Review release",
  };
  return labels[actionType] || "Review update";
}

function actionVerb(actionType) {
  return {
    disposition: "Outcome saved",
    reply: "Reply queued",
    escalation: "Escalated",
    assignment: "Assignment changed",
    release: "Released",
  }[actionType] || "Updated";
}

export function buildManagerReviewQueue(actions = [], options = {}) {
  const settings = Object.assign({ dashboard: null, limit: 5 }, options);
  const actionItems = (Array.isArray(actions) ? actions : [])
    .filter((item) => item && item.conversation_id)
    .filter((item) => (item.source_kind || "manual") !== "automation")
    .map((item) => {
      const actionType = inferActionType(item);
      return {
        conversationId: item.conversation_id,
        contactName: cleanText(item.contact_name, "Unknown contact"),
        label: reviewLabel(actionType),
        value: titleCase(actionType),
        tone: reviewTone(actionType),
        actionType,
        summary: cleanText(item.summary, "Queue update needs review."),
        occurredAt: item.occurred_at,
        meta: `${cleanText(item.summary, "Queue update needs review.")} | ${cleanText(item.actor_full_name || item.actor_email, "team action")}`,
        actionLabel: "Open review",
      };
    });

  const deduped = [];
  const seen = new Set();
  for (const item of actionItems) {
    const key = `${item.conversationId}:${item.actionType}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push(item);
  }

  const items = deduped.slice(0, Math.max(1, settings.limit));
  const outcomeCount = items.filter((item) => item.actionType === "disposition").length;
  const replyCount = items.filter((item) => item.actionType === "reply").length;
  const openTotal = toInt(((settings.dashboard || {}).summary || {}).open_total);

  return {
    items,
    summaryCards: [
      {
        label: "Review lane",
        value: String(items.length),
        detail: items.length ? "Recent operator-visible work waiting for supervisory scan." : "No recent operator work needs review.",
        tone: items.length ? "stale" : "ready",
      },
      {
        label: "Outcome checks",
        value: String(outcomeCount),
        detail: outcomeCount ? "Disposition changes should be confirmed before the next touch." : "No outcome changes in the review lane.",
        tone: outcomeCount ? "overdue" : "muted",
      },
      {
        label: "Reply checks",
        value: String(replyCount),
        detail: `${openTotal} open conversation(s) in the manager queue context.`,
        tone: replyCount ? "stale" : "muted",
      },
    ],
  };
}

export function buildManagerVisibilityBrief(actions = [], options = {}) {
  const settings = Object.assign({ dashboard: null, reviewQueue: null, limit: 4 }, options);
  const reviewQueue = settings.reviewQueue || buildManagerReviewQueue(actions, { dashboard: settings.dashboard });
  const manualActions = (Array.isArray(actions) ? actions : [])
    .filter((item) => item && item.conversation_id)
    .filter((item) => (item.source_kind || "manual") !== "automation")
    .slice(0, Math.max(1, settings.limit));
  const urgentReviewCount = (reviewQueue.items || []).filter((item) => item.tone === "overdue").length;
  const replyCount = manualActions.filter((item) => inferActionType(item) === "reply").length;
  const outcomeCount = manualActions.filter((item) => inferActionType(item) === "disposition").length;
  const openTotal = toInt(((settings.dashboard || {}).summary || {}).open_total);
  const overdueTotal = toInt(((settings.dashboard || {}).sla || {}).overdue_total);
  return {
    title: "Manager Visibility",
    statusLabel: urgentReviewCount
      ? `${urgentReviewCount} urgent review${urgentReviewCount === 1 ? "" : "s"}`
      : reviewQueue.items.length
        ? `${reviewQueue.items.length} review item${reviewQueue.items.length === 1 ? "" : "s"}`
        : "All clear",
    tone: urgentReviewCount ? "overdue" : reviewQueue.items.length ? "stale" : "ready",
    detail: reviewQueue.items.length
      ? "Recent operator work is ready for supervisory scan before the next touch."
      : "No recent operator work is waiting for manager review.",
    summaryCards: [
      {
        label: "Review now",
        value: String(reviewQueue.items.length),
        detail: urgentReviewCount ? `${urgentReviewCount} outcome or risk review(s) should be opened first.` : "Open review items from this lane.",
        tone: urgentReviewCount ? "overdue" : reviewQueue.items.length ? "stale" : "ready",
      },
      {
        label: "Operator changes",
        value: String(manualActions.length),
        detail: `${replyCount} reply signal(s), ${outcomeCount} outcome signal(s) in the latest feed.`,
        tone: manualActions.length ? "ready" : "muted",
      },
      {
        label: "Queue risk",
        value: String(overdueTotal),
        detail: `${openTotal} open conversation(s) in manager context.`,
        tone: overdueTotal ? "overdue" : "ready",
      },
    ],
    rows: manualActions.map((item) => {
      const actionType = inferActionType(item);
      return {
        conversationId: item.conversation_id,
        contactName: cleanText(item.contact_name, "Unknown contact"),
        label: actionVerb(actionType),
        value: titleCase(actionType),
        summary: cleanText(item.summary, "Operator-visible queue update."),
        actor: cleanText(item.actor_full_name || item.actor_email, "team action"),
        occurredAt: item.occurred_at,
        tone: reviewTone(actionType),
        actionLabel: reviewLabel(actionType),
      };
    }),
  };
}

export function buildManagerReviewCommandBrief(options = {}) {
  const settings = Object.assign(
    {
      reviewQueue: null,
      visibilityBrief: null,
      dashboard: null,
      commandWorkspace: null,
      limit: 3,
    },
    options
  );
  const reviewQueue = settings.reviewQueue || { items: [], summaryCards: [] };
  const visibilityBrief = settings.visibilityBrief || { rows: [] };
  const dashboard = settings.dashboard || {};
  const commandWorkspace = settings.commandWorkspace || {};
  const reviewItems = Array.isArray(reviewQueue.items) ? reviewQueue.items : [];
  const visibilityRows = Array.isArray(visibilityBrief.rows) ? visibilityBrief.rows : [];
  const urgentItems = reviewItems.filter((item) => item.tone === "overdue");
  const firstReview = urgentItems[0] || reviewItems[0] || null;
  const openTotal = toInt(((dashboard || {}).summary || {}).open_total);
  const overdueTotal = toInt(((dashboard || {}).sla || {}).overdue_total);
  const workloadUsers = Array.isArray(commandWorkspace.workloadRows) ? commandWorkspace.workloadRows : [];
  const heaviestUser = workloadUsers[0] || null;
  const limitedRows = reviewItems.slice(0, Math.max(1, settings.limit));

  return {
    title: "Review command brief",
    statusLabel: firstReview ? `${urgentItems.length || reviewItems.length} review signal${(urgentItems.length || reviewItems.length) === 1 ? "" : "s"}` : "Review lane clear",
    tone: urgentItems.length ? "overdue" : reviewItems.length ? "stale" : "ready",
    detail: firstReview
      ? `${firstReview.contactName} should be scanned first so operator work, outcome, and next touch stay aligned.`
      : "No operator work is waiting for manager review. Keep an eye on queue risk and workload pressure.",
    primaryAction: firstReview
      ? {
          label: "Open manager review",
          conversationId: firstReview.conversationId,
          tone: firstReview.tone,
        }
      : {
          label: overdueTotal ? "Review overdue queue" : "Review open queue",
          viewKey: overdueTotal ? "overdue" : "open",
          tone: overdueTotal ? "overdue" : "ready",
        },
    cards: [
      {
        label: "Next manager action",
        value: firstReview ? firstReview.label : "Monitor queue",
        detail: firstReview ? firstReview.summary : "No review item is currently blocking the lane.",
        tone: firstReview ? firstReview.tone : "ready",
      },
      {
        label: "Review lane",
        value: String(reviewItems.length),
        detail: urgentItems.length ? `${urgentItems.length} urgent outcome review(s).` : "Reply and outcome review count.",
        tone: urgentItems.length ? "overdue" : reviewItems.length ? "stale" : "ready",
      },
      {
        label: "Queue pressure",
        value: String(overdueTotal),
        detail: `${openTotal} open conversation(s) in the manager context.`,
        tone: overdueTotal ? "overdue" : "ready",
      },
      {
        label: "Workload focus",
        value: heaviestUser ? heaviestUser.fullName : "No active load",
        detail: heaviestUser ? `${heaviestUser.openAssignedCount} open assigned for ${heaviestUser.role}.` : "No active workload leader returned.",
        tone: heaviestUser && heaviestUser.openAssignedCount ? "stale" : "muted",
      },
    ],
    rows: limitedRows.length
      ? limitedRows.map((item, index) => {
          const matchingVisibility = visibilityRows.find((row) => row.conversationId === item.conversationId) || null;
          return {
            rank: index + 1,
            conversationId: item.conversationId,
            contactName: item.contactName,
            label: item.label,
            value: item.value,
            tone: item.tone,
            summary: matchingVisibility ? matchingVisibility.summary : item.summary,
            actor: matchingVisibility ? matchingVisibility.actor : cleanText(item.meta, "operator activity"),
            actionLabel: item.actionLabel || "Open review",
          };
        })
      : [
          {
            rank: 1,
            conversationId: null,
            contactName: "No review item",
            label: "Queue scan",
            value: overdueTotal ? "Overdue" : "Clear",
            tone: overdueTotal ? "overdue" : "ready",
            summary: overdueTotal ? "Open the overdue queue and confirm response coverage." : "No recent operator review signal is waiting.",
            actor: "manager command",
            actionLabel: overdueTotal ? "Review overdue queue" : "Review open queue",
          },
        ],
  };
}

export function buildManagerConversationReview(options = {}) {
  const settings = Object.assign(
    {
      conversation: null,
      qualification: null,
      messages: [],
      tasks: [],
      notes: [],
      recentActions: [],
      currentUser: null,
    },
    options
  );
  const conversation = settings.conversation || {};
  const qualification = settings.qualification || {};
  const notes = Array.isArray(settings.notes) ? settings.notes : [];
  const messages = Array.isArray(settings.messages) ? settings.messages : [];
  const tasks = Array.isArray(settings.tasks) ? settings.tasks : [];
  const recentActions = Array.isArray(settings.recentActions) ? settings.recentActions : [];
  const qualificationReady = toInt(qualification.capturedCount) >= 4;
  const outboundReply = messages.find((item) => String(item.direction || "").toLowerCase() === "outbound") || null;
  const outcomeAction = recentActions.find((item) => inferActionType(item) === "disposition") || null;
  const latestReview = notes.find((item) => item.note_type === "manager_review") || null;
  const latestGuidance = notes.find((item) => item.note_type === "manager_guidance") || null;
  const openManagerTasks = tasks.filter((item) => item.status !== "completed" && item.task_type === "manager_review");
  const nextStepField = (qualification.fields || []).find((item) => item.key === "next_step");
  const nextStep = cleanText(nextStepField && nextStepField.value, "No manager next step captured yet.");
  const needsReview = Boolean(qualificationReady || outboundReply || outcomeAction) && !latestReview;
  const latestOperatorAction = recentActions.find((item) => (item.source_kind || "manual") !== "automation") || null;
  const changedValue = outcomeAction
    ? "Outcome changed"
    : outboundReply
      ? "Reply queued"
      : qualificationReady
        ? "Qualification ready"
        : "Monitoring";

  return {
    statusLabel: latestReview ? "Reviewed" : needsReview ? "Needs review" : "Monitoring",
    tone: latestReview ? "ready" : needsReview ? "overdue" : "muted",
    summaryCards: [
      {
        label: "Operator work",
        value: qualificationReady ? "Qualified" : outboundReply ? "Replied" : "Watching",
        detail: qualificationReady ? "Qualification is ready for manager scan." : "Review reply and activity before approving next action.",
        tone: qualificationReady || outboundReply ? "ready" : "muted",
      },
      {
        label: "Manager review",
        value: latestReview ? "Complete" : needsReview ? "Open" : "Queued",
        detail: latestReview ? cleanText(latestReview.body, "Manager reviewed this record.") : "Use manager actions to record the supervisory decision.",
        tone: latestReview ? "ready" : needsReview ? "overdue" : "stale",
      },
      {
        label: "Next step",
        value: nextStep,
        detail: openManagerTasks.length ? `${openManagerTasks.length} manager review task(s) open.` : "No manager review task is open.",
        tone: openManagerTasks.length ? "stale" : nextStepField && nextStepField.value ? "ready" : "muted",
      },
    ],
    changeCards: [
      {
        label: "What changed",
        value: changedValue,
        detail: outcomeAction
          ? cleanText(outcomeAction.summary, "Disposition changed and needs manager scan.")
          : outboundReply
            ? cleanText(outboundReply.body, "Operator queued an outbound reply.")
            : qualificationReady
              ? "Operator captured enough qualification context for review."
              : "No new operator action requires a manager decision yet.",
        tone: outcomeAction ? "overdue" : outboundReply || qualificationReady ? "stale" : "muted",
      },
      {
        label: "Operator handoff",
        value: nextStep,
        detail: latestOperatorAction
          ? `${cleanText(latestOperatorAction.actor_full_name || latestOperatorAction.actor_email, "Team member")} | ${cleanText(latestOperatorAction.summary, "Latest queue action")}`
          : "No recent manual queue action is attached to this review.",
        tone: nextStepField && nextStepField.value ? "ready" : "muted",
      },
      {
        label: "Manager decision",
        value: latestReview ? "Reviewed" : needsReview ? "Review now" : "Monitor",
        detail: latestReview
          ? cleanText(latestReview.body, "Manager review saved.")
          : needsReview
            ? "Mark reviewed, return guidance, or assign a manager follow-up."
            : "Keep watching until operator work creates a review signal.",
        tone: latestReview ? "ready" : needsReview ? "overdue" : "muted",
      },
    ],
    rows: [
      {
        label: "Qualification",
        value: `${qualification.capturedCount || 0}/${qualification.totalCount || 5}`,
        tone: qualificationReady ? "ready" : "stale",
        meta: qualificationReady ? "Operator captured enough context for review." : "Qualification needs more seller context.",
      },
      {
        label: "Latest reply",
        value: outboundReply ? "Queued" : "No reply",
        tone: outboundReply ? "ready" : "muted",
        meta: outboundReply ? cleanText(outboundReply.body, "Outbound reply saved.") : "No outbound reply is visible in this thread.",
      },
      {
        label: "Latest outcome",
        value: outcomeAction ? "Disposition" : "No outcome",
        tone: outcomeAction ? "overdue" : "muted",
        meta: outcomeAction ? cleanText(outcomeAction.summary, "Disposition changed.") : "No disposition action is visible for this record.",
      },
      {
        label: "Latest guidance",
        value: latestGuidance ? "Saved" : "None",
        tone: latestGuidance ? "stale" : "muted",
        meta: latestGuidance ? cleanText(latestGuidance.body, "Manager guidance saved.") : "Return guidance to the operator if the next step needs revision.",
      },
    ],
    actions: [
      {
        key: "mark_reviewed",
        label: "Mark Reviewed",
        kind: "note",
        noteType: "manager_review",
        tone: "ready",
        body: `Manager reviewed ${cleanText(conversation.contact_name, "this record")}. Approved next step: ${nextStep}`,
      },
      {
        key: "return_guidance",
        label: "Return Guidance",
        kind: "note",
        noteType: "manager_guidance",
        tone: "stale",
        body: `Manager guidance for ${cleanText(conversation.contact_name, "this record")}: tighten qualification and confirm next step before advancing.`,
      },
      {
        key: "assign_follow_up",
        label: "Assign Follow-Up",
        kind: "task",
        tone: "overdue",
        taskType: "manager_review",
        title: `Manager review follow-up: ${cleanText(conversation.contact_name, "lead")}`,
        notes: `Review operator qualification, latest reply, and disposition. Proposed next step: ${nextStep}`,
        assignedUserId: (settings.currentUser || {}).user_id || conversation.assigned_user_id || null,
        priority: "high",
      },
    ],
  };
}
